import type { DemoRoster } from '../api/demoRoster.js';
import { useDemoResetInFlightSlug } from './demoResetInFlight.js';
import { findSlot } from './roster.js';
import { useDemoRosterQuery } from './useDemoRosterQuery.js';

export type DemoCopyResetState =
  | { type: 'idle' }
  | { type: 'resetting'; slug: string; displayName: string }
  | { type: 'failed'; slug: string; displayName: string; errorSummary: string }
  | { type: 'settled'; slug: string; displayName: string };

export function deriveDemoCopyResetState(input: {
  patientUserId: string;
  roster: DemoRoster | undefined;
  inFlightSlug: string | undefined;
}): DemoCopyResetState {
  const { patientUserId, roster, inFlightSlug } = input;
  if (inFlightSlug === undefined) {
    return { type: 'idle' };
  }

  const slot = findSlot(roster, inFlightSlug);
  const displayName = slot?.display_name ?? '';

  if (slot === undefined || slot.state.type === 'seeding') {
    return { type: 'resetting', slug: inFlightSlug, displayName };
  }

  if (slot.state.type === 'ready' && slot.state.patient_user_id === patientUserId) {
    return { type: 'settled', slug: inFlightSlug, displayName };
  }

  if (slot.state.type === 'failed') {
    return {
      type: 'failed',
      slug: inFlightSlug,
      displayName,
      errorSummary: slot.state.error_summary,
    };
  }

  return { type: 'resetting', slug: inFlightSlug, displayName };
}

export function useDemoCopyResetState(patientUserId: string): DemoCopyResetState {
  const { data: roster } = useDemoRosterQuery();
  const inFlightSlug = useDemoResetInFlightSlug(patientUserId);
  return deriveDemoCopyResetState({
    patientUserId,
    roster,
    inFlightSlug,
  });
}
